import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { type MailboxInfo } from "owa-client-types";
import { getOptionsForFeature, MonarchKeyboardShortcutsMode, OwsOptionsFeatureType, type KeyboardShortcutsOptions, } from "owa-outlook-service-option-store";
import { getOcpsPolicyNumericValue, isUserAllowedToChangeSettings, KeyboardStylesPolicySettingId, OcpsPolicyFeature, } from "owa-owsprime-policies-store";
import { type default as WebKeyboardShortcutsMode } from "owa-service/lib/contract/KeyboardShortcutsMode";
import getUserConfiguration from "owa-session-store/lib/actions/getUserConfiguration";
import shouldUseMonarchShortcuts from "./shouldUseMonarchShortcuts";

// These mappings are based on the enum values from the CPS admin policy schema.
// See https://clients.config.office.net/settings/v1.0/settingscatalog/settings?idSubstring=L_WebKeyboardShortcutStyle.
const WEB_CPS_MODE_TO_CLIENT_MODE = new Map<number, WebKeyboardShortcutsMode>([
    [0, 0],
    [1, 2],
    [2, 3],
    [3, 4],
    [4, 1],
]);

// See https://clients.config.office.net/settings/v1.0/settingscatalog/settings?idSubstring=L_WindowsKeyboardShortcutStyle.
const MONARCH_CPS_MODE_TO_CLIENT_MODE = new Map<number, MonarchKeyboardShortcutsMode>([
    [0, MonarchKeyboardShortcutsMode.OutlookForWindows],
    [1, MonarchKeyboardShortcutsMode.OutlookForWeb],
    [2, MonarchKeyboardShortcutsMode.Off],
]);

export default function getKeyboardShortcutsMode(): WebKeyboardShortcutsMode | MonarchKeyboardShortcutsMode {
    const useMonarch = shouldUseMonarchShortcuts();
    
    // OCPS admin policy: when policies are enabled and the admin disallows user override,
    // force the policy-configured shortcut style (Monarch -> Windows style, Web -> Web style).
    // An absent or out-of-range policy value falls through to the OutlookForWindows or Owa based on the platform.
    const globalMailboxInfo = getGlobalSettingsAccountMailboxInfo();
    if (!isUserAllowedToChangeSettings(OcpsPolicyFeature.KeyboardStyles, globalMailboxInfo)) {
        return getPolicyValueOrDefault(useMonarch, globalMailboxInfo);
    }
    
    // If the user is allowed to change settings, return the user's configured value.
    // If the user hasn't configured the shortcuts then choose admin set defaults if configured or fallback to platform specific default.
    const userConfiguredShortcutMode = useMonarch
        ? getOptionsForFeature<KeyboardShortcutsOptions>(OwsOptionsFeatureType.KeyboardShortcuts)
            .keyboardShortcutsMode
        : getUserConfiguration().UserOptions?.KeyboardShortcutsMode;
    
    return userConfiguredShortcutMode ?? getPolicyValueOrDefault(useMonarch, globalMailboxInfo);
}

function getPolicyValueOrDefault(useMonarch: boolean, mailboxInfo: MailboxInfo): WebKeyboardShortcutsMode | MonarchKeyboardShortcutsMode {
    const settingId = useMonarch
        ? KeyboardStylesPolicySettingId.OutlookWindowsKeyboardShortcutStyle
        : KeyboardStylesPolicySettingId.OutlookWebKeyboardShortcutStyle;
    const policyValue = getOcpsPolicyNumericValue(settingId, mailboxInfo);
    const defaultMode = useMonarch
        ? MonarchKeyboardShortcutsMode.OutlookForWindows
        : 4;
    
    if (useMonarch) {
        return ((policyValue != undefined
            ? MONARCH_CPS_MODE_TO_CLIENT_MODE.get(policyValue)
            : undefined) ?? defaultMode);
    }
    else {
        return ((policyValue != undefined ? WEB_CPS_MODE_TO_CLIENT_MODE.get(policyValue) : undefined) ??
            defaultMode);
    }
}
